import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { service } from '@ember/service';
import { fn, hash } from '@ember/helper';
import { t } from 'ember-intl';
import { on } from '@ember/modifier';
import { gt, eq } from 'ember-truth-helpers';
import { HashicorpCloudResourcemanagerPolicyBindingMemberType } from '@clients/cloud-resource-manager';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import {
  HdsTextDisplay,
  HdsButton,
  HdsIcon,
  HdsTextBody,
  HdsButtonSet,
} from '@hashicorp/design-system-components/components';
import { Flex, FlexGrid, WithErrors, StickyFooter } from 'core/components';
import { getLiteral } from 'core/helpers';
import { boxPadding, boxMargin } from 'core/modifiers';
import PolicyManager from 'manage-access/utils/policy-manager';
import EditPermissions from 'manage-access/components/manage-access/form/edit-permissions';
import RoleDefinitions from 'manage-access/components/manage-access/flyout/role-definitions';

/**
 *
 * `IamGroupsProjectFormSelectRoleV2Component` encapsulates the form used to edit a groups's role at the project level.
 *
 * ```
 * <Iam::Groups::Project::Form::SelectGroupRole
 *   @currentRolesLocation={{@currentRolesLocation}}
 *   @currentScopeLink={{@currentScopeLink}}
 *   @groups={{@groups}}
 *   @projectRoles={{@projectRoles}}
 *   @policy={{@policy}}
 *   @project={{@project}}
 *   @groupIdToRoleIdMappings={{this.groupIdToRoleIdMappings}}
 *   @handleSubmit={{this.handleSelectGroupRoleSubmit}}
 * />
 * ```
 *
 * @class IamGroupsProjectFormSelectRoleV2Component
 *
 */
export default class IamGroupsProjectFormSelectRoleV2 extends Component {
  @service api;
  @service router;

  @tracked editedPolicy;
  @tracked showRoleDefinitionsFlyout = false;

  knownGroupIds = Object.keys(this.args?.groupIdToRoleIdMappings ?? {});

  didInsert = () => {
    const { policy } = this.args;
    this.setEditedPolicy(policy);
  };

  didUpdateGroupIdToRoleIdMappings = () => {
    const { groupIdToRoleIdMappings = {} } = this.args;
    const newKnownGroupIds = Object.keys(groupIdToRoleIdMappings);
    const removedGroupIds = this.knownGroupIds.filter((knownGroupId) => {
      return !newKnownGroupIds.includes(knownGroupId);
    });
    const EditedPolicy = new PolicyManager({ policy: this.editedPolicy });

    for (let groupId of removedGroupIds) {
      const member = {
        memberId: groupId,
        memberType: HashicorpCloudResourcemanagerPolicyBindingMemberType.GROUP,
      };
      const { roleIds = [] } = EditedPolicy.getRolesByMember(member) ?? {};

      for (let roleId of roleIds) {
        EditedPolicy.removeMemberFromRole(member, [roleId]);
      }
    }

    this.knownGroupIds = newKnownGroupIds;
    this.setEditedPolicy(EditedPolicy.getRawPolicy());
  };

  setEditedPolicy(policy) {
    const { groupIdToRoleIdMappings = {} } = this.args;

    const DefaultPolicy = new PolicyManager({ policy });

    for (let [memberId, roleId] of Object.entries(groupIdToRoleIdMappings)) {
      const member = {
        memberId: memberId,
        memberType: HashicorpCloudResourcemanagerPolicyBindingMemberType.GROUP,
      };
      const { roleIds = [] } = DefaultPolicy.getRolesByMember(member) ?? {};

      if (!roleIds.length) {
        DefaultPolicy.addMemberToRole(member, [roleId]);
      }
    }

    this.editedPolicy = DefaultPolicy.getRawPolicy();
  }

  onPolicyChange = ({ policy }) => {
    this.editedPolicy = policy;
  };

  removeGroup = (resourceId) => {
    const { removeGroup } = this.args;

    if (removeGroup && typeof removeGroup === 'function') {
      removeGroup(resourceId);
    }
  };

  submit = task(async (event) => {
    event.preventDefault();

    await this.args.handleSubmit.perform(this.editedPolicy);
  });

  <template>
    <form
      data-test-iam-groups-project-form-select-group-role
      aria-label={{t
        "components.iam.groups.project.form.select-group-role.description"
      }}
      class="hcp-hds-form"
      id="add-groups"
      novalidate
      {{on "submit" this.submit.perform}}
      {{didInsert this.didInsert}}
      {{didUpdate
        this.didUpdateGroupIdToRoleIdMappings
        @groupIdToRoleIdMappings
      }}
    >
      <WithErrors @error={{this.submit.last.error}} as |FormError|>
        {{#if this.submit.last.error}}
          <div {{boxMargin "0 0 md 0"}} data-test-add-groups-error>
            <FormError />
          </div>
        {{/if}}

        <Flex @direction="column" @gap="sm">
          <HdsTextDisplay
            @color="strong"
            @size="200"
            @tag="h3"
            @weight="semibold"
          >
            {{t
              "components.iam.groups.project.form.select-group-role.assign.label"
            }}
          </HdsTextDisplay>

          {{#each @groups key="resourceId" as |group|}}
            {{! We loop over groups because we can control the order of the array
          which doesn't cause spookiness in the rendering of the dropdowns.
          Also, we need to ensure that group is set in the mappings before
          rendering it.}}
            {{#let
              group.resourceId
              (getLiteral @groupIdToRoleIdMappings group.resourceId)
              as |resourceId roleId|
            }}
              {{#if roleId}}
                <Flex @direction="column" @gap="lg">
                  <FlexGrid data-test-iam-groups-add-row as |G|>
                    <G.Item @xs="12" @sm="12" @md="3" @lg="2" @xl="2">
                      <Flex
                        @alignItems="center"
                        @gap="xs"
                        {{boxMargin "0 0 md 0"}}
                      >
                        <HdsIcon @name="users" @isInline={{true}} />
                        <HdsTextBody
                          @color="strong"
                          @size="200"
                          @weight="semibold"
                        >
                          {{group.displayName}}
                        </HdsTextBody>
                      </Flex>
                    </G.Item>
                    <G.Item
                      @xs="12"
                      @sm="12"
                      @md="9"
                      @lg="10"
                      @xl="10"
                      data-test-select-group-role-v2
                    >
                      <EditPermissions
                        @memberId={{resourceId}}
                        @memberType="GROUP"
                        @policy={{this.editedPolicy}}
                        @roles={{@projectRoles}}
                        @onChange={{this.onPolicyChange}}
                        data-test-iam-groups-project-edit-permissions
                      >
                        <:body as |B|>
                          <FlexGrid
                            class="bottom-xs bottom-sm bottom-md bottom-lg"
                            as |G|
                          >
                            <G.Item @xs="12" @sm="12" @md="5" @lg="6" @xl="6">
                              <Flex
                                @alignItems="start"
                                @direction="row"
                                @gap="sm"
                                @basis="zero-percent"
                                @grow={{1}}
                                @shrink={{1}}
                              >
                                <B.PermissionSelect
                                  @basicServiceLabel={{t
                                    "components.iam.groups.project.form.select-group-role.edit-assignment.project-label"
                                    name=@project.name
                                  }}
                                />
                              </Flex>
                            </G.Item>
                            <G.Item @xs="12" @sm="12" @md="7" @lg="6" @xl="6">
                              <Flex
                                @alignItems="end"
                                @direction="row"
                                @gap="sm"
                                @basis="zero-percent"
                                @grow={{1}}
                                @shrink={{1}}
                                {{boxPadding "xs 0 0 0"}}
                              >
                                <HdsButton
                                  @color="tertiary"
                                  @icon="help"
                                  @iconPosition="leading"
                                  @isInline={{true}}
                                  @text={{t
                                    "components.iam.groups.project.form.select-group-role.buttons.view-role-definitions"
                                  }}
                                  {{on
                                    "click"
                                    (set this "showRoleDefinitionsFlyout" true)
                                  }}
                                />
                                <HdsButton
                                  @color="tertiary"
                                  @icon="trash"
                                  @text={{if
                                    (gt B.data.permissions.size 1)
                                    B.removePermissionText
                                    (t
                                      "components.iam.groups.project.form.select-group-role.buttons.remove-group"
                                    )
                                  }}
                                  {{on
                                    "click"
                                    (queue
                                      (fn
                                        B.removePermission
                                        B.data.id
                                        B.data.permission
                                      )
                                      (if
                                        (eq B.data.currentRoleIds.length 0)
                                        (fn this.removeGroup group.resourceId)
                                      )
                                    )
                                  }}
                                  data-test-iam-groups-remove-assignment-button={{B.data.id}}
                                />
                              </Flex>
                            </G.Item>
                          </FlexGrid>
                        </:body>
                        <:footer as |F|>
                          {{#if F.nextAvailableRole.service}}
                            <HdsButtonSet>
                              <HdsButton
                                @color="tertiary"
                                @icon="plus"
                                @text={{F.addAssignmentText}}
                                {{on
                                  "click"
                                  (fn
                                    F.addPermission
                                    (hash service=F.nextAvailableRole.service)
                                  )
                                }}
                                data-test-iam-groups-add-assignment-button
                              />
                            </HdsButtonSet>
                          {{/if}}
                        </:footer>
                      </EditPermissions>
                    </G.Item>
                  </FlexGrid>
                </Flex>
              {{/if}}
            {{/let}}
          {{/each}}
        </Flex>

        <StickyFooter>
          <HdsButtonSet>
            <HdsButton
              @color="primary"
              @text={{t
                "components.iam.groups.project.form.select-group-role.buttons.submit"
              }}
              disabled={{this.submit.isRunning}}
              form="add-groups"
              type="submit"
              data-test-add-groups-button
            />

            <HdsButton
              @color="secondary"
              @text={{t
                "components.iam.groups.project.form.select-group-role.buttons.cancel"
              }}
              @route="cloud.access-control.groups.list"
              data-test-cancel
            />
          </HdsButtonSet>
        </StickyFooter>
        {{#if this.showRoleDefinitionsFlyout}}
          <RoleDefinitions
            @rolesLocation={{@currentRolesLocation}}
            @currentScopeLink={{@currentScopeLink}}
            @onClose={{set this "showRoleDefinitionsFlyout" false}}
          />
        {{/if}}
      </WithErrors>
    </form>
  </template>
}
